'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';
import LiveWhiteboard, { type DemoSelection } from '@/components/whiteboard-v2/LiveWhiteboard';
import type { StoredProblem } from '@/components/whiteboard-v2/types';
import useAuthStore from '@/store/useAuthStore';
import useSubscriptionStore from '@/store/useSubscriptionStore';
import { isStudentProfile } from '@/lib/profile-role';
import PaywallModal from '@/components/Subscription/PaywallModal';

interface LearnLiveTutorModalProps {
  problem?: StoredProblem;
  selection?: DemoSelection;
  isOpen: boolean;
  onClose: () => void;
}

export default function LearnLiveTutorModal({
  problem,
  selection,
  isOpen,
  onClose,
}: LearnLiveTutorModalProps) {
  const { user, profile } = useAuthStore();
  const { consumeQuota } = useSubscriptionStore();
  const [quotaStatus, setQuotaStatus] = useState<'pending' | 'allowed' | 'blocked'>('pending');
  const lastCheckedOpenRef = useRef(false);

  const resolvedSelection = useMemo<DemoSelection | null>(
    () => selection || (problem ? { kind: 'problem', problem } : null),
    [problem, selection],
  );

  // Check quota when modal opens (only for students)
  useEffect(() => {
    if (!isOpen || lastCheckedOpenRef.current) return;
    lastCheckedOpenRef.current = true;

    if (!user?.id || !isStudentProfile(profile)) {
      // Not a student or not logged in — skip quota check, allow immediately
      setQuotaStatus('allowed');
      return;
    }

    let cancelled = false;
    setQuotaStatus('pending');
    void (async () => {
      const result = await consumeQuota('live_tutor', user.id);
      if (cancelled) return;
      setQuotaStatus(result.allowed ? 'allowed' : 'blocked');
    })();

    return () => { cancelled = true; };
  }, [isOpen, user?.id, profile, consumeQuota]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      lastCheckedOpenRef.current = false;
      setQuotaStatus('pending');
    }
  }, [isOpen]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleClose();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [handleClose, isOpen]);

  if (!isOpen || !resolvedSelection) {
    return null;
  }

  // Show paywall if quota is exhausted
  if (quotaStatus === 'blocked') {
    return (
      <PaywallModal visible feature="live_tutor" onClose={handleClose} />
    );
  }

  // Show loading overlay while quota check is in progress
  if (quotaStatus === 'pending') {
    return (
      <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/55 backdrop-blur-sm">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-white border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[1200] bg-black/55 backdrop-blur-sm p-2 sm:p-5" onClick={handleClose}>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          handleClose();
        }}
        className="absolute right-3 top-3 z-[60] rounded-full bg-white/95 p-2 text-[color:var(--gray-700)] shadow-md hover:bg-white sm:right-5 sm:top-5"
        aria-label="Close live tutor"
      >
        <X className="h-5 w-5" />
      </button>

      <div className="relative flex h-full items-center justify-center overflow-auto" onClick={(event) => event.stopPropagation()}>
        <LiveWhiteboard selection={resolvedSelection} />
      </div>
    </div>
  );
}
